import {AfterViewInit, Component, OnDestroy, QueryList, ViewChildren} from '@angular/core';
import {Subscription} from "rxjs";
import {IInstallationDataResponse} from "../../../../../../interfaces/installation-data.response";
import {DataService} from "../../../../../../services/data.service";
import {DeviceDataService} from "../../../../../../services/device-data.service";
import {AlertService} from "../../../../../../services/alert.service";
import {NgModel} from "@angular/forms";
import {ShareService} from "../../../../../../services/share.service";
import {ValidationService} from "../../../../../../services/validation.service";
import {debounceTime} from "rxjs/operators";
import {GeneralSendingModel} from '../../../../../../models/data-models/general-sending-model';
import {TemperatureSensorNameEnum} from '../../../../../../enums/temperature-sensor-name-enum';
import {PumpSensorNameEnum} from '../../../../../../enums/pump-sensor-name-enum';
import {CounterSensorNameEnum} from '../../../../../../enums/counter-sensor-name-enum';
import {OtherSensorNameEnum} from "../../../../../../enums/other-sensor-name-enum";
import {InstallationSubmissionData} from '../../../../../../models/installation-submission-data';

@Component({
  selector: 'app-install-general-sending',
  templateUrl: './install-general-sending.component.html',
  styleUrls: ['./install-general-sending.component.scss'],

})
export class InstallGeneralSendingComponent implements AfterViewInit, OnDestroy {
  public model: GeneralSendingModel;

  public installationData: IInstallationDataResponse;

  @ViewChildren(NgModel) inputs: QueryList<NgModel>;

  public sensorTypes = [
    'Temperaturen',
    'Pumpen',
    'Zähler',
    'Sonstige'
  ];

  public sensorNameOptionsMap: { [key: string]: string[] };

  private readonly installationDataSub: Subscription;
  private readonly modelSub: Subscription;
  private inputSubscriptions: Subscription[] = [];

  constructor(
    private dataService: DataService,
    private deviceDataService: DeviceDataService,
    private alertService: AlertService,
    private shareService: ShareService,
    public validationService: ValidationService
  ) {
    this.installationDataSub = this.deviceDataService.installationData$.subscribe(data => {
      this.installationData = data;
    });
    this.model = this.shareService.generalSending || new GeneralSendingModel();
    this.modelSub = this.shareService.generalSending$.subscribe((data) => {
      this.model = data || new GeneralSendingModel();
    });

    this.sensorNameOptionsMap = {
      'Temperaturen': Object.values(TemperatureSensorNameEnum),
      'Pumpen': Object.values(PumpSensorNameEnum),
      'Zähler': Object.values(CounterSensorNameEnum),
      'Sonstige': Object.values(OtherSensorNameEnum),
    };
  }

  ngAfterViewInit(): void {
    this.inputs.forEach(input => {
      this.inputSubscriptions.push(input.control.valueChanges.pipe(
        debounceTime(500)
      ).subscribe(() => {
        this.shareService.generalSending = this.model;
      }));
    });

    // Subscribe to changes in the list of inputs
    // (it is called if some input is added or removed from the form/page,
    // for example, because of *ngIf condition changes)
    this.inputs.changes.subscribe(() => {
      // Unsubscribe from old subscriptions
      this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());

      // Subscribe to new inputs
      this.inputs.forEach(input => {
        this.inputSubscriptions.push(input.control.valueChanges.pipe(
          debounceTime(500)
        ).subscribe(() => {
          this.shareService.generalSending = this.model;
        }));
      });
    });
  }

  ngOnDestroy() {
    this.inputSubscriptions.forEach(subscription => subscription.unsubscribe());
    if (this.installationDataSub) {
      this.installationDataSub.unsubscribe();
    }
    if (this.modelSub) {
      this.modelSub.unsubscribe();
    }
  }

  async sendMontage() {
    if (this.canSendMontage()) {
      const deviceData = this.deviceDataService.getDeviceData().deviceData;
      if (deviceData) {
        for (let module of deviceData.uvsModule) {
          for (let item of module.w1 || []) {
            if (item.temperature.value === 85 || item.temperature.value === 127.9) {
              this.alertService.error('Mind. ein Sensor liefert einen Temperatur-Fehlerwert (127.9 oder 85.0). Erneuere die Leitung oder tausche den Sensor und lade die Daten erneut.');
              return;
            }
          }
        }
      }

      console.log("Sending montage data...");

      // Build InstallationSubmissionData directly from ShareService
      const submissionData: InstallationSubmissionData = {
        gatewayId: this.shareService.getGatewayId(),
        simIp: this.shareService.simIp,
        technicianEmail: null,
        installationAt: new Date().toISOString(),

        building: this.shareService.building,
        generalGateway: this.shareService.generalGateway,
        generalMeasurement: this.shareService.generalMeasurement,
        generalPrePhotos: this.shareService.generalPrePhotos,
        generalSending: this.shareService.generalSending,
        heatingCheck: this.shareService.heatingCheck,
        heatingInformation: this.shareService.heatingInformation,
        photos: this.shareService.photos,
        sensorsCounter: this.shareService.sensorsCounter,
        sensorsOther: this.shareService.sensorsOther,
        sensorsOutsideTemp: this.shareService.sensorsOutsideTemp,
        sensorsPump: this.shareService.sensorsPump,
        sensorsTemperature: this.shareService.sensorsTemperature,
      };

      console.log(submissionData);
      await this.dataService.postOngoingData(submissionData, this.deviceDataService.getDevice(), this.deviceDataService.getDeviceData().deviceData);

      this.deviceDataService.setInstallationData(await this.dataService.getGatewayBySerialNumber(this.shareService.getGatewayId()));

      this.alertService.success("Montage-Daten erfolgreich gesendet.");
    }
  }

  canSendMontage(): boolean {
    return this.validationService.isGeneralValid(this.shareService.generalGateway, this.shareService.generalMeasurement, this.shareService.generalPrePhotos, this.shareService.generalSending)
      && this.deviceDataService.getDevice()?.readInfo?.gatewayInfo?.snr?.length > 0
      && this.deviceDataService.getDeviceData()?.deviceData !== undefined
      && this.deviceDataService.getDeviceData()?.deviceData !== null;
  }

  addNewSensor() {
    this.model.nonInstallableSensors.push({
      type: '',
      sensorName: '',
      reason: ''
    });
  }

  removeSensor(index: number) {
    this.model.nonInstallableSensors.splice(index, 1);
  }
}
